import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ReplyDialog
 * -----------
 * Compose a reply to one email in the request's thread. Owns its own draft
 * text; the parent supplies `onSend(body)`, an async action that performs
 * the send (and any reload) and throws on failure.
 *
 * `initialBody` seeds the draft when the dialog opens (used for the
 * "Send a reminder" action on an overdue request). A draft the user has
 * typed is kept if they cancel.
 */
const ReplyDialog = ({ open, onOpenChange, email, onSend, initialBody, title }) => {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && initialBody && !body) setBody(initialBody);
    // Only seed when the dialog opens; the user's typing wins after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBody]);

  const handleSend = async () => {
    if (!body.trim()) {
      toast.error('Write something first.');
      return;
    }

    setSending(true);
    try {
      await onSend(body);
      toast.success('Reply sent. We will tell you when they answer.');
      setBody('');
      onOpenChange(false);
    } catch {
      toast.error('Could not send the reply. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title || `Reply to ${email?.from_address || 'them'}`}</DialogTitle>
          <DialogDescription>
            {email?.subject ? `RE: ${email.subject}` : 'This goes to the organisation by email.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {email?.ai_suggested_action && (
            <div className="rounded-lg bg-amber-wash px-4 py-3 text-[15px] leading-[1.6] text-amber-ink">
              <span className="font-semibold">Suggested: </span>
              {email.ai_suggested_action}
            </div>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your reply..."
            className="min-h-[220px] text-base"
            aria-label="Your reply"
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-reply-btn">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            Send Reply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReplyDialog;
